import { cn } from '@/shared/utils/cn';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type Ref,
} from 'react';
import { RANGE_BAR_TRACK_BLEED_X } from '../../constants/rangeBarLayout';
import type {
  RangeBarAccessibilityProps,
  RangeBarAppearanceProps,
  RangeBarColor,
  RangeBarEventsProps,
  RangeBarRangeProps,
  RangeBarValue,
} from '../../types';
import {
  clamp,
  clampEndAgainstStart,
  clampStartAgainstEnd,
  normalizeRangeValue,
  snapToStep,
} from '../../utils/rangeValue';

const TRACK_H = 'ui-h-8';
/** Píxeles de movimiento antes de decidir start vs end cuando los thumbs están encimados. */
const COLLAPSED_DRAG_THRESHOLD_PX = 4;

const FILL_BY_COLOR: Record<RangeBarColor, string> = {
  brand: 'ui-bg-fill-brand',
  /** Figma «neutro»: barra activa oscura alineada a texto/icono principal. */
  neutral: 'ui-bg-[var(--ui-color-text-primary)]',
};

const THUMB_INNER_BY_COLOR: Record<RangeBarColor, string> = {
  brand: 'ui-bg-fill-brand',
  neutral: 'ui-bg-[var(--ui-color-icon-default)]',
};

export interface RangeBarProps {
  /** Dominio y valor del intervalo. */
  range: RangeBarRangeProps;
  /** Emisión de cambios de valor. */
  events: RangeBarEventsProps;
  /** Variante cromática del relleno y punto interior. */
  appearance?: RangeBarAppearanceProps;
  /** Etiquetas de grupo y por thumb. */
  accessibility?: RangeBarAccessibilityProps;
  /** Deshabilita interacción y atenua la pista. */
  disabled?: boolean;
  /**
   * Sangrado horizontal (`RANGE_BAR_TRACK_BLEED_X`, 10px) para que los thumbs de 20px no se recorten
   * en 0% / 100%. En layouts muy estrechos (p. ej. `RangeSelector` compacto) se puede desactivar.
   * @default true
   */
  trackBleed?: boolean;
  /** Clases en el contenedor externo (ref). */
  className?: string;
}

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as MutableRefObject<T | null>).current = node;
  }
}

/**
 * **RangeBar** — pista con dos thumbs para elegir un intervalo `[start, end]` dentro de `[min, max]`.
 *
 * Basado en Figma *Range Bar* (`1:983`): pista 8px, radios pill, thumbs 20px con borde y punto interior.
 * Flujos: arrastre de extremos, click en pista (mueve el thumb más cercano), teclado en thumbs (flechas/Home/End).
 * Cuando los dos thumbs coinciden, no se cruzan (sin intercambio). Si arrastras encimados: hacia la
 * **izquierda** actúa el extremo **start** (y recibe foco); hacia la **derecha** el **end**. Un clic
 * en la pista sin arrastre sigue acercando el valor al punto pulsado.
 *
 * @example
 * ```tsx
 * const [v, setV] = useState({ start: 2.5, end: 4 });
 * <RangeBar
 *   range={{ min: 0, max: 5, step: 0.5, value: v }}
 *   events={{ onChange: setV }}
 *   appearance={{ color: 'brand' }}
 * />
 * ```
 */
export const RangeBar = forwardRef<HTMLDivElement, RangeBarProps>(function RangeBar(
  {
    range,
    events,
    appearance: appearanceProp,
    accessibility: accessibilityProp,
    disabled = false,
    trackBleed = true,
    className,
  },
  ref
) {
  const { min, max, value, step = 1 } = range;
  const { onChange } = events;
  const { color = 'brand' } = appearanceProp ?? {};
  const {
    'aria-label': ariaLabel = 'Selector de rango',
    getThumbAriaLabel = defaultThumbAriaLabel,
  } = accessibilityProp ?? {};
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'start' | 'end' | null>(null);
  /** Thumbs encimados: esperando dirección del primer movimiento (o clic sin arrastre en pista). */
  const collapsedPickPendingRef = useRef(false);
  const collapsedDragOriginXRef = useRef<number | null>(null);
  const thumbStartRef = useRef<HTMLButtonElement>(null);
  const thumbEndRef = useRef<HTMLButtonElement>(null);
  const safeRef = useRef(normalizeRangeValue(value, min, max, step));
  const baseId = useId();
  const [activeThumb, setActiveThumb] = useState<'start' | 'end' | null>(null);

  const safe = normalizeRangeValue(value, min, max, step);
  safeRef.current = safe;

  const isCollapsed = safe.start === safe.end;

  const focusThumb = useCallback((which: 'start' | 'end') => {
    const el = which === 'start' ? thumbStartRef.current : thumbEndRef.current;
    el?.focus({ preventScroll: true });
  }, []);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      assignRef(ref, node);
    },
    [ref]
  );

  const valueFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return min;
      const rect = el.getBoundingClientRect();
      const t = rect.width <= 0 ? 0 : (clientX - rect.left) / rect.width;
      const raw = min + clamp(t, 0, 1) * (max - min);
      return snapToStep(raw, min, max, step);
    },
    [max, min, step]
  );

  const applyThumb = useCallback(
    (which: 'start' | 'end', numeric: number) => {
      const next =
        which === 'start'
          ? clampStartAgainstEnd(numeric, safe.end, min, max, step)
          : clampEndAgainstStart(safe.start, numeric, min, max, step);
      if (next.start !== safe.start || next.end !== safe.end) {
        onChange(next);
      }
    },
    [max, min, onChange, safe.end, safe.start, step]
  );

  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    const v = valueFromClientX(e.clientX);
    if (isCollapsed) {
      collapsedPickPendingRef.current = true;
      collapsedDragOriginXRef.current = e.clientX;
      draggingRef.current = null;
      setActiveThumb(null);
    } else {
      const distStart = Math.abs(v - safe.start);
      const distEnd = Math.abs(v - safe.end);
      const which: 'start' | 'end' = distStart <= distEnd ? 'start' : 'end';
      applyThumb(which, v);
      draggingRef.current = which;
      setActiveThumb(which);
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onThumbPointerDown =
    (which: 'start' | 'end') => (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (disabled || e.button !== 0) return;
      e.stopPropagation();
      if (isCollapsed) {
        collapsedPickPendingRef.current = true;
        collapsedDragOriginXRef.current = e.clientX;
        draggingRef.current = null;
        setActiveThumb(null);
      } else {
        draggingRef.current = which;
        setActiveThumb(which);
      }
      e.currentTarget.setPointerCapture(e.pointerId);
    };

  const flushPointerMove = useCallback(
    (e: Pick<ReactPointerEvent<Element>, 'clientX'>) => {
      if (disabled) return;
      if (collapsedPickPendingRef.current && collapsedDragOriginXRef.current !== null) {
        const dx = e.clientX - collapsedDragOriginXRef.current;
        if (Math.abs(dx) >= COLLAPSED_DRAG_THRESHOLD_PX) {
          const which: 'start' | 'end' = dx < 0 ? 'start' : 'end';
          draggingRef.current = which;
          collapsedPickPendingRef.current = false;
          collapsedDragOriginXRef.current = null;
          setActiveThumb(which);
          focusThumb(which);
        } else {
          return;
        }
      }
      const which = draggingRef.current;
      if (!which) return;
      applyThumb(which, valueFromClientX(e.clientX));
    },
    [applyThumb, disabled, focusThumb, valueFromClientX]
  );

  const applyThumbRef = useRef(applyThumb);
  applyThumbRef.current = applyThumb;
  const valueFromClientXRef = useRef(valueFromClientX);
  valueFromClientXRef.current = valueFromClientX;

  useEffect(() => {
    const onMove = (e: PointerEvent) => flushPointerMove(e);
    const onUp = () => {
      if (collapsedPickPendingRef.current && draggingRef.current === null) {
        const originX = collapsedDragOriginXRef.current;
        if (originX !== null) {
          const s = safeRef.current;
          const w = valueFromClientXRef.current(originX);
          if (w < s.start) {
            applyThumbRef.current('start', w);
          } else if (w > s.end) {
            applyThumbRef.current('end', w);
          }
        }
      }
      collapsedPickPendingRef.current = false;
      collapsedDragOriginXRef.current = null;
      draggingRef.current = null;
      setActiveThumb(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [flushPointerMove]);

  const pct = (n: number) => ((n - min) / (max - min || 1)) * 100;
  const pStart = pct(safe.start);
  const pEnd = pct(safe.end);
  const fillLeft = pStart;
  const fillWidth = Math.max(0, pEnd - pStart);

  const nudge = (which: 'start' | 'end', delta: number) => {
    const base = which === 'start' ? safe.start : safe.end;
    applyThumb(which, base + delta);
  };

  const onKeyDown = (which: 'start' | 'end') => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const stepSize = step > 0 ? step : (max - min) / 100;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        nudge(which, -stepSize);
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        nudge(which, stepSize);
        break;
      case 'Home':
        e.preventDefault();
        applyThumb(which, min);
        break;
      case 'End':
        e.preventDefault();
        applyThumb(which, max);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={setRefs} className={cn('ui-imj ui-w-full', className)}>
      <div
        role="group"
        aria-label={ariaLabel}
        className={cn(
          'ui-relative ui-w-full',
          trackBleed && RANGE_BAR_TRACK_BLEED_X,
          disabled && 'ui-pointer-events-none ui-opacity-50'
        )}
      >
        <div
          ref={trackRef}
          className={cn(
            'ui-relative ui-w-full',
            TRACK_H,
            'ui-rounded-control-pill ui-bg-fill-tertiary',
            !disabled && 'ui-cursor-pointer'
          )}
          onPointerDown={onTrackPointerDown}
        >
          <div
            className={cn(
              'ui-pointer-events-none ui-absolute ui-inset-y-0 ui-my-auto ui-h-8 ui-rounded-control-pill',
              FILL_BY_COLOR[color]
            )}
            style={{
              left: `${fillLeft}%`,
              width: `${fillWidth}%`,
            }}
          />
          {(['start', 'end'] as const).map((which) => {
            const pos = which === 'start' ? pStart : pEnd;
            const val = which === 'start' ? safe.start : safe.end;
            const id = `${baseId}-${which}`;
            return (
              <button
                key={which}
                ref={which === 'start' ? thumbStartRef : thumbEndRef}
                type="button"
                id={id}
                tabIndex={disabled ? -1 : 0}
                aria-label={getThumbAriaLabel(which, val)}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={val}
                aria-valuetext={String(val)}
                role="slider"
                disabled={disabled}
                className={cn(
                  'ui-absolute ui-top-1/2 ui-z-10 ui-flex ui-h-[20px] ui-w-[20px] -ui-translate-y-1/2 ui-translate-x-[-50%] ui-items-center ui-justify-center',
                  'ui-rounded-control-pill ui-border-1 ui-border-default ui-bg-fill-secondary',
                  'focus-visible:ui-ring-brand ui-p-0 ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-offset-2',
                  !disabled && 'ui-cursor-grab active:ui-cursor-grabbing',
                  activeThumb === which && 'ui-z-20'
                )}
                style={{ left: `${pos}%` }}
                onPointerDown={onThumbPointerDown(which)}
                onKeyDown={onKeyDown(which)}
              >
                <span
                  className={cn(
                    'ui-block ui-h-[10px] ui-w-[10px] ui-rounded-full',
                    THUMB_INNER_BY_COLOR[color]
                  )}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
});

RangeBar.displayName = 'RangeBar';

function defaultThumbAriaLabel(which: 'start' | 'end', numericValue: number): string {
  if (which === 'start') {
    return `Mínimo del rango, ${numericValue}`;
  }
  return `Máximo del rango, ${numericValue}`;
}
